import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { googleBigQueryAuthTypes } from 'app/model/linkedservice/google-bigquery.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { userAuthPassControls } from './google-bigquery-metadata';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { cdcDestination, parameterizeDynamicsControlMetadata } from '../consts/metadata-consts';
import { ConnectionAuthKind } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

export enum GoogleBigQueryV2Versions {
    V1_0 = '1.0',
    V1_1 = '1.1'
}

const serviceAuthControls: ControlMetadata[] = [
    {
        type: 'dynamic-akv-switcher',
        bind: 'keyFileContent',
        label: stringResources.ModelName_KeyFile,
        child: {
            type: 'key-file-content',
            label: stringResources.ModelName_KeyFile,
            required: true,
            isLabelVertical: true,
            readAsText: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            stringResources.GoogleBigQuery_DisplayText)
    }
];

const versionControl: ControlMetadata = {
    type: 'radio',
    bind: 'version',
    label: stringResources.VersionLabel,
    isLabelVertical: true,
    hide: !GlobalVariables.enableGBQVersion,
    options: [
        {
            label: GoogleBigQueryV2Versions.V1_1,
            value: GoogleBigQueryV2Versions.V1_1,
            description: format(stringResources.Connector_Version_Description, 'https://learn.microsoft.com/azure/data-factory/connector-google-bigquery?tabs=data-factory'),
            escapeDescription: false
        },
        {
            label: GoogleBigQueryV2Versions.V1_0,
            value: undefined
        }
    ],
    defaultValue: GoogleBigQueryV2Versions.V1_1,
}

const commonControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'projectId',
        label: stringResources.ModelName_ProjectID,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
];

export const googleBigQueryV2Metadata: IDynamicLinkedServiceTypeMetadata = async () => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: __IS_FABRIC_DPE__ ? adfModelTypeMap.googleBigQuery.linkedServiceType : adfModelTypeMap.googleBigQueryV2.linkedServiceType,
        displayName: stringResources.GoogleBigQuery_DisplayText,
        linkedServiceNamePrefix: adfModelTypeMap.googleBigQuery.linkedServiceType,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-google_bigquery'
        },
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'GoogleBigQuery' as ResourceKind
        },
        supportedAuthKindsInTrident: [
            ConnectionAuthKind.Basic // , `ConnectionAuthKind.OAuth2` should be supported in the future
        ],
        supportFirstClassParameterExperience: true,
        isFirstClass: true,
        category: ['Database'],
        supportDatasetV2: true,
        supportOPDG: GlobalVariables.enableOPDG,
        supportOPDGInMirror: GlobalVariables.mirrorGBQOPDG,
        supportVNETInMirror: GlobalVariables.mirrorGBQOPDG, // We're using the same FS to enable both OPDG and VNET
        supportCopyJobSource: true,
        supportCopyJobSink: __IS_FABRIC_DPE__,
        cdc: {
            supportCDC: true,
            supportedCDCDestinations: cdcDestination,
            tableIcon: {
                regular: 'icon-table_text_cdc_20_regular',
                filled: 'icon-table_text_cdc_20_filled'
            },
            GA: true
        },
        supportMultipleIncrementalColumns: true,
        copySinkTypes: __IS_FABRIC_DPE__ ? [adfModelTypeMap.googleBigQuery.copySinkType] : [],
        copySourceTypes: [__IS_FABRIC_DPE__ ? adfModelTypeMap.googleBigQuery.copySourceType : adfModelTypeMap.googleBigQueryV2.copySourceType],
        controls: [
            versionControl,
            ...(commonControls.map(parameterizeDynamicsControlMetadata)),
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.ModelName_AuthenticationType,
                options: [
                    {
                        label: stringResources.ModelName_ServiceAuthentication,
                        value: googleBigQueryAuthTypes.ServiceAuthentication
                    },
                    {
                        label: stringResources.ModelName_UserAuthentication_NoPreview,
                        value: googleBigQueryAuthTypes.UserAuthentication
                    }
                ],
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                isLabelVertical: true,
                defaultValue: googleBigQueryAuthTypes.UserAuthentication,
                cases: [
                    {
                        caseValue: googleBigQueryAuthTypes.ServiceAuthentication,
                        children: serviceAuthControls.map(parameterizeDynamicsControlMetadata)
                    },
                    {
                        caseValue: googleBigQueryAuthTypes.UserAuthentication,
                        children: userAuthPassControls.map(parameterizeDynamicsControlMetadata)
                    }
                ]
            }
        ]
    };
    return metadata;
};
